// BUILD-046 correction cycle 1bb88b3e (stage B2b, items N5 and N6): "Done with note" - the small control beside Done, its compact inline panel, and the
// messages of finished completions.
//
// ORDINARY DONE IS UNCHANGED: the labelled green Done button is one click, one request, no body (MyWorkRowParts.DoneControl). "Done with note" is a second,
// nearby, TEXT-labelled control (no unexplained icon): a disclosure button with an accessible name, aria-haspopup and aria-expanded, and the tooltip
// "Complete this task and record a note". It opens a COMPACT panel under the row with an optional note ("Completion note (optional)"), an "Activity date"
// that defaults to today (Phoenix) and cannot be set later than today, an explicit "Complete task" button and "Cancel". Typed entry always works; there is no
// dictation in this local preview. Escape closes the panel and KEEPS the draft; Cancel discards it.
//
// The words are the model's (doneNoteModel.ts, tested): "Completed. Note saved." only when the server said noteSaved === true; "Completed. Note not saved."
// with the kept text when it did not; "Not completed. Your note is kept. Try again." when the request failed. A source-governed task shows the existing
// "completes through its source" text instead of either control (the model's `governedText`). This file draws; it decides nothing.
import { useEffect, useRef } from 'react';
import { ACTIVITY_DATE_LABEL } from './dateLabels';
import { COMPLETE_TASK_LABEL, DONE_WITH_NOTE_LABEL, DONE_WITH_NOTE_TOOLTIP, NOTE_FIELD_LABEL } from './doneNoteModel';
import type { DoneNoteOffer, DoneNoteView, NoteNotice } from './doneNoteModel';
// D1 display cleanup: the task title in a notice is shown without the "(FICTIONAL)" test marker.
import { stripTestMarker } from './displayNames';

/** What a row needs to draw the control and the panel. The host (the page) owns the state and the requests. */
export interface DoneNoteControls {
  offer: DoneNoteOffer;
  view: DoneNoteView;
  /** Opens the panel, or closes it keeping the draft (the button is a toggle). */
  onToggle: () => void;
  /** Escape: closes the panel keeping the draft, and returns focus to the button. */
  onClose: () => void;
  onText: (text: string) => void;
  onDate: (date: string) => void;
  onComplete: () => void;
  /** Cancel: discards the draft on purpose. */
  onCancel: () => void;
}

const panelIdOf = (rowId: string) => `done-note-${rowId}`;
const buttonIdOf = (rowId: string) => `done-note-button-${rowId}`;

export interface DoneWithNoteButtonProps {
  rowId: string;
  title: string;
  controls: DoneNoteControls;
  compact: boolean;
  /**
   * `caret` (layout revision, Layout-4): the ADJACENT dropdown caret of the split Done button - a small green button joined to Done, icon only, with the same
   * accessible name ("Done with note: <title>"), the same tooltip, aria-haspopup="dialog" and aria-expanded. `standalone` (default) is the text button.
   */
  variant?: 'standalone' | 'caret';
}

/** The control beside Done, or (for a source-governed task the Caller owns) the existing explanation. Nothing for any other row. */
export function DoneWithNoteButton({ rowId, title, controls, compact, variant = 'standalone' }: DoneWithNoteButtonProps) {
  const { offer, view } = controls;
  if (!offer.offered) {
    return offer.governedText === null ? null : (
      <span className="text-xs text-gray-600" data-testid="completes-through-source">
        {offer.governedText}
      </span>
    );
  }
  if (variant === 'caret') {
    return (
      <button
        id={buttonIdOf(rowId)}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={view.open}
        aria-controls={view.open ? panelIdOf(rowId) : undefined}
        aria-label={`${DONE_WITH_NOTE_LABEL}: ${title}`}
        title={DONE_WITH_NOTE_TOOLTIP}
        onClick={controls.onToggle}
        className={`${compact ? 'px-1.5 py-1' : 'px-2 py-1.5'} inline-flex items-center rounded-r-md border border-green-800 bg-green-700 text-white hover:bg-green-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-green-700`}
      >
        <svg aria-hidden="true" viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 7.5l5 5 5-5" />
        </svg>
      </button>
    );
  }
  return (
    <button
      id={buttonIdOf(rowId)}
      type="button"
      aria-haspopup="dialog"
      aria-expanded={view.open}
      aria-controls={view.open ? panelIdOf(rowId) : undefined}
      aria-label={`${DONE_WITH_NOTE_LABEL}: ${title}`}
      title={DONE_WITH_NOTE_TOOLTIP}
      onClick={controls.onToggle}
      className={`${compact ? 'px-2 py-1' : 'px-2.5 py-1.5'} inline-flex items-center gap-1 text-xs font-medium text-green-800 bg-white border border-green-600 rounded hover:bg-green-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-green-600`}
    >
      <span>{DONE_WITH_NOTE_LABEL}</span>
      <span aria-hidden="true" className="text-[10px]">
        &#9662;
      </span>
    </button>
  );
}

export interface DoneNotePanelProps {
  rowId: string;
  title: string;
  controls: DoneNoteControls;
}

/** The compact panel under the row. Nothing while it is closed. */
export function DoneNotePanel({ rowId, title, controls }: DoneNotePanelProps) {
  const { view } = controls;
  const textRef = useRef<HTMLTextAreaElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const id = panelIdOf(rowId);

  // Opening puts the cursor in the note and brings the WHOLE panel into view (its buttons are at the bottom, below a row near the edge of the window).
  useEffect(() => {
    if (!view.open) return;
    textRef.current?.focus({ preventScroll: true });
    panelRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [view.open]);
  // A message that arrives under the buttons (a failure, a refused date) must not sit below the fold.
  const outcomeKind = view.outcome?.kind ?? null;
  const dateError = view.fieldErrors.activityDate;
  useEffect(() => {
    if (view.open && (outcomeKind !== null || dateError !== null)) panelRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [view.open, outcomeKind, dateError]);

  if (!view.open) return null;
  const closeToButton = () => {
    controls.onClose();
    document.getElementById(buttonIdOf(rowId))?.focus();
  };
  const outcome = view.outcome;
  const outcomeClass = outcome === null ? '' : outcome.tone === 'success' ? 'bg-green-50 text-green-900 border-green-200' : outcome.tone === 'warning' ? 'bg-amber-50 text-amber-900 border-amber-200' : 'bg-red-50 text-red-800 border-red-200';

  return (
    <div
      ref={panelRef}
      id={id}
      role="dialog"
      aria-modal="false"
      aria-label={`${DONE_WITH_NOTE_LABEL}: ${title}`}
      data-testid="done-note-panel"
      className="mt-2 w-full space-y-2 rounded border border-green-200 bg-green-50/40 p-3 text-xs"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          closeToButton();
        }
      }}
    >
      <div>
        <label htmlFor={`${id}-text`} className="mb-1 block font-medium text-gray-700">
          {NOTE_FIELD_LABEL}
        </label>
        <textarea
          ref={textRef}
          id={`${id}-text`}
          rows={2}
          value={view.text}
          disabled={view.complete.label !== COMPLETE_TASK_LABEL}
          onChange={(event) => controls.onText(event.target.value)}
          aria-describedby={view.fieldErrors.text !== null ? `${id}-text-error` : undefined}
          aria-invalid={view.fieldErrors.text !== null || undefined}
          className="w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#4AABDE]"
        />
        {view.fieldErrors.text !== null && (
          <p id={`${id}-text-error`} role="alert" className="mt-1 text-red-700">
            {view.fieldErrors.text}
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
        <div>
          <label htmlFor={`${id}-date`} className="mb-1 block font-medium text-gray-700">
            {ACTIVITY_DATE_LABEL}
          </label>
          <input
            id={`${id}-date`}
            type="date"
            value={view.activityDate}
            max={view.maxDate}
            disabled={view.complete.label !== COMPLETE_TASK_LABEL}
            onChange={(event) => controls.onDate(event.target.value)}
            aria-describedby={view.fieldErrors.activityDate !== null ? `${id}-date-error` : undefined}
            aria-invalid={view.fieldErrors.activityDate !== null || undefined}
            className="rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#4AABDE]"
          />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            disabled={view.complete.disabled}
            onClick={controls.onComplete}
            className="rounded border border-green-800 bg-green-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-green-700 disabled:opacity-50"
          >
            {view.complete.label}
          </button>
          <button
            type="button"
            disabled={view.complete.label !== COMPLETE_TASK_LABEL}
            onClick={() => {
              controls.onCancel();
              document.getElementById(buttonIdOf(rowId))?.focus();
            }}
            className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-800 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE] disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
      {view.fieldErrors.activityDate !== null && (
        <p id={`${id}-date-error`} role="alert" className="text-red-700">
          {view.fieldErrors.activityDate}
        </p>
      )}

      {outcome !== null && (
        <div role={outcome.tone === 'success' ? 'status' : 'alert'} className={`rounded border px-2 py-1.5 ${outcomeClass}`} data-testid="done-note-outcome">
          <p className="font-medium">{outcome.headline}</p>
          {outcome.detail !== null && <p className="mt-0.5">{outcome.detail}</p>}
          {view.needsReload && <p className="mt-0.5">The list is being read again.</p>}
        </div>
      )}
    </div>
  );
}

export interface NoteNoticesProps {
  notices: readonly NoteNotice[];
  titleOf: (id: string) => string;
  /** Dismisses the message. For a notice that carries a kept note the host also discards the kept text (it is shown here, once, and can be copied first). */
  onDismiss: (id: string, hadDraft: boolean) => void;
}

/**
 * The messages of completions whose row has left the list (a completed task leaves the Active tab) or whose panel is closed: "Completed. Note saved.",
 * "Completed. No note added.", or a warning that the note was NOT saved with the text that was kept.
 */
export function NoteNotices({ notices, titleOf, onDismiss }: NoteNoticesProps) {
  if (notices.length === 0) return null;
  return (
    <ul className="mb-3 space-y-1" aria-label="Completion notes" data-testid="note-notices">
      {notices.map((notice) => {
        const tone = notice.outcome.tone === 'success' ? 'bg-green-50 text-green-900 border-green-200' : notice.outcome.tone === 'warning' ? 'bg-amber-50 text-amber-900 border-amber-200' : 'bg-red-50 text-red-800 border-red-200';
        return (
          <li key={notice.id} role={notice.outcome.tone === 'success' ? 'status' : 'alert'} className={`rounded border px-3 py-2 text-sm ${tone}`}>
            <div className="flex items-start justify-between gap-2">
              <p>
                <span className="font-medium">{stripTestMarker(titleOf(notice.id))}</span>: {notice.outcome.headline}
                {notice.outcome.detail !== null && <span className="ml-1">{notice.outcome.detail}</span>}
              </p>
              <button type="button" onClick={() => onDismiss(notice.id, notice.draft !== null)} aria-label={notice.draft !== null ? 'Dismiss message and the kept note' : 'Dismiss message'} className="font-bold">
                &times;
              </button>
            </div>
            {notice.draft !== null && (
              <textarea readOnly aria-label="Your note, kept" value={notice.draft} rows={2} className="mt-1 w-full rounded border border-amber-300 bg-white px-2 py-1 text-xs text-gray-900" />
            )}
          </li>
        );
      })}
    </ul>
  );
}
